import React, { useMemo, useState } from "react";
import { Calculator } from "lucide-react";
import { inr } from "../api";

function Row({ label, val, onChange, suffix, min, max, step }) {
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-neutral-600">{label}</span>
        <span className="font-semibold text-navy">{suffix === "₹" ? inr(val) : `${val}${suffix}`}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={val} onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-brand-500" data-testid={`emi-${label.toLowerCase().replace(/[^a-z]/g, "")}`} />
    </div>
  );
}

export default function EMICalculator({ price = 5000000 }) {
  const [amount, setAmount] = useState(Math.round(price));
  const [down, setDown] = useState(20);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);

  const { emi, principal, totalInterest, total } = useMemo(() => {
    const p = Math.max(amount - (amount * down) / 100, 0);
    const r = rate / 12 / 100;
    const n = years * 12;
    const e = r === 0 ? p / n : (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const tot = e * n;
    return { emi: e || 0, principal: p, totalInterest: tot - p, total: tot };
  }, [amount, down, rate, years]);

  return (
    <div className="bg-surface border border-neutral-200 rounded-2xl p-6 shadow-card" data-testid="emi-calculator">
      <h3 className="flex items-center gap-2 text-xl font-semibold text-navy mb-4">
        <Calculator className="w-5 h-5 text-brand-500" /> Home Loan EMI Calculator
      </h3>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
        <div className="space-y-5">
          <Row label="Property Price" val={amount} onChange={setAmount} suffix="₹" min={500000} max={100000000} step={100000} />
          <Row label="Down Payment" val={down} onChange={setDown} suffix="%" min={0} max={90} step={1} />
          <Row label="Interest Rate" val={rate} onChange={setRate} suffix="%" min={5} max={15} step={0.1} />
          <Row label="Tenure" val={years} onChange={setYears} suffix=" yrs" min={1} max={30} step={1} />
        </div>
        <div className="flex flex-col justify-center bg-neutral-50 rounded-xl p-6 text-center">
          <p className="text-sm text-neutral-500">Monthly EMI</p>
          <p className="text-3xl font-bold text-brand-600 my-1" data-testid="emi-monthly">
            ₹{Math.round(emi).toLocaleString("en-IN")}
          </p>
          <div className="grid grid-cols-2 gap-2 mt-4 text-left text-sm">
            <div><p className="text-neutral-500">Loan Amount</p><p className="font-semibold text-navy">{inr(principal)}</p></div>
            <div><p className="text-neutral-500">Total Interest</p><p className="font-semibold text-navy">{inr(totalInterest)}</p></div>
            <div className="col-span-2"><p className="text-neutral-500">Total Payable</p><p className="font-semibold text-navy">{inr(total)}</p></div>
          </div>
        </div>
      </div>
      <p className="text-xs text-neutral-400 mt-4">*Indicative only. Actual EMI depends on your lender.</p>
    </div>
  );
}
